import { useState, useMemo, useEffect } from "react";
import styles from "../styles/CreatorOrders.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { listUsersOrdersByCreator } from "../../../actions/orderActions";
import Spinner from "../../../components/Spinner";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import DateRangePicker from "../../../components/DateRange/DateRange";
import { DateRange } from "react-day-picker";


interface OrderItem {
  _id: string;
  id: string;
  name: string;
  price: number;
  selectedSize?: string;
  photo?: { url: string };
  creatorId?: string;
  originalPrice?: number | null;
  omnibusLowestPrice?: number | null;
  showPromotionInfo?: boolean;
  showOmnibusInfo?: boolean;
}

interface ApiOrder {
  _id: string;
  customerName?: string;
  customerEmail?: string;
  createdAt: string;
  updatedAt?: string;
  orderItems: OrderItem[];
  user?: string;             
  totalPrice: number;
  orderStatus: string;
  isPaid: boolean;
  paymentMethod?: string;
  commissionRate?: number;
  commissionAmount?: number;
  creatorPayout?: number;
  paymentResult?: { reason: string | null; provider: string | null };
  statusReason?: string | null;
  __v?: number;
}

type Status = "Completed" | "Pending" | "Refunded";

interface Order {
  id: string;
  buyerEmail: string;
  buyerName: string;
  date: string;
  rawDate: Date;
  customer: string;
  product: string;
  value: number;
  payout: number;
  commission: number;
  paymentMethod: string;
  isPaid: boolean;
  status: Status;
}

type SortKey = "id" | "date" | "value" | "payout";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 10;


function normaliseStatus(raw: string): Status {
  const s = (raw ?? "").toLowerCase();
  if (s === "completed") return "Completed";
  if (s === "refunded")  return "Refunded";
  return "Pending";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function mapApiOrder(o: ApiOrder): Order {
  const items = o.orderItems ?? [];

  const customer = o.customerName || "Unknown";

  const productLabel =
    items.length === 0
      ? "—"
      : items.length === 1
      ? `${items[0].name}${items[0].selectedSize ? ` (${items[0].selectedSize.toUpperCase()})` : ""}`
      : `${items[0].name} (+${items.length - 1} more)`;

  return {
    id:            o._id,
    date:          formatDate(o.createdAt),
    rawDate:       new Date(o.createdAt),
    customer,
    buyerName: o.customerName || "Unknown",
    buyerEmail: o.customerEmail || "Unknown", 
    product:       productLabel,
    value:         o.totalPrice       ?? 0,
    payout:        o.creatorPayout    ?? 0,
    commission:    o.commissionAmount ?? 0,
    paymentMethod: o.paymentMethod    ?? "—",
    isPaid:        o.isPaid           ?? false,
    status:        normaliseStatus(o.orderStatus),
  };
}


interface SearchSectionProps {
  query: string;
  status: string;
  onQueryChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  resultCount: number;
  totalCount: number;
  range?: DateRange;
  setRange: (r: DateRange | undefined) => void;
  handleApply: () => void;
  isYearMenuOpen: boolean;
  setIsYearMenuOpen: (open: boolean) => void;
}

function SearchSection({ query, status, onQueryChange, onStatusChange, resultCount, totalCount, range, setRange, handleApply, isYearMenuOpen, setIsYearMenuOpen }: SearchSectionProps) {
  return (
    <div className={styles.searchSection}>
      <div className={styles.searchWrapper}>
        <span className={styles.searchIcon}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
          </svg>
        </span>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by order ID, customer, or product..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>
            <div className={styles.dateRangeWrapper}>
                <PrimaryButton

                  onClick={() => setIsYearMenuOpen(!isYearMenuOpen)}>
                  {range?.from && range?.to
                    ? `${range.from.toDateString()} → ${range.to.toDateString()}`
                    : "Select date Range"}
                </PrimaryButton>


              {/* Popup calendar */}
              {isYearMenuOpen && (
                <div
                  className={styles.popupCalender}
                >
                  <DateRangePicker
                    range={range}
                    setRange={setRange}
                  />

                  <div className={styles.calenderFooter}>
                    <button onClick={() => setIsYearMenuOpen(false)} className={styles.calenderButton}>Cancel</button>
                    <button onClick={handleApply} className={styles.calenderButton}>Apply</button>
                  </div>
                </div>

              )}
              </div>

      <div className={styles.selectWrapper}>
        <select className={styles.statusSelect} value={status} onChange={(e) => onStatusChange(e.target.value)}>
          <option value="">All Status</option>
          <option value="Completed">Completed</option>
          <option value="Pending">Pending</option>
          <option value="Refunded">Refunded</option>
        </select>
        <span className={styles.selectIcon}>
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>

      <span className={styles.resultCount}>
        Showing {resultCount} of {totalCount} orders
      </span>
    </div>
  );
}


function StatusBadge({ status }: { status: Status }) {
  const cls =
    status === "Completed" ? styles.completed :
    status === "Pending"   ? styles.pending   :
                             styles.refunded;
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}

// ─── OrdersTable ──────────────────────────────────────────────────────────────

interface OrdersTableProps {
  orders: Order[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}

function OrdersTable({ orders, sortKey, sortDir, onSort }: OrdersTableProps) {
  const arrow = (key: SortKey) => (
    <span className={styles.sortIcon}>
      {sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  return (
    <div className={styles.tableCard}>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={`${styles.th} ${styles.sortable}`} onClick={() => onSort("id")}>Order ID {arrow("id")}</th>
              <th className={`${styles.th} ${styles.sortable}`} onClick={() => onSort("date")}>Date {arrow("date")}</th>
              <th className={styles.th}>Customer</th>
              <th className={styles.th}>Product</th>
              <th className={`${styles.th} ${styles.sortable} ${styles.alignRight}`} onClick={() => onSort("value")}>Total {arrow("value")}</th>
              <th className={`${styles.th} ${styles.sortable} ${styles.alignRight}`} onClick={() => onSort("payout")}>Your Payout {arrow("payout")}</th>
              <th className={`${styles.th} ${styles.alignCenter}`}>Payment</th>
              <th className={`${styles.th} ${styles.alignCenter}`}>Status</th>
            </tr>
          </thead>
          <tbody className={styles.noOdersRow}>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.emptyState}>No orders found.</td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr key={o.id} className={styles.tr}>
                  <td className={`${styles.td} ${styles.orderId}`}>{o.id.slice(-10)}</td>
                  <td className={`${styles.td} ${styles.date}`}>{o.date}</td>
                  <td className={`${styles.td} ${styles.customer}`}>{o.customer}</td>
                  <td className={`${styles.td} ${styles.product}`}>{o.product}</td>
                  <td className={`${styles.td} ${styles.value}`}>${o.value.toFixed(2)}</td>
                  <td className={`${styles.td} ${styles.value}`}>
                    <span className={styles.payoutAmount}>${o.payout.toFixed(2)}</span>
                    <span className={styles.commissionNote}> (−${o.commission.toFixed(2)} fee)</span>
                  </td>
                  <td className={`${styles.td} ${styles.alignCenter}`}>
                    <span className={`${styles.paymentBadge} ${o.isPaid ? styles.paid : styles.unpaid}`}>
                      {o.isPaid ? "Paid" : "Unpaid"} · {o.paymentMethod}
                    </span>
                  </td>
                  <td className={`${styles.td} ${styles.status}`}>
                    <StatusBadge status={o.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileList}>
        {orders.length === 0 ? (
          <p className={styles.mobileEmpty}>No orders found.</p>
        ) : (
          orders.map((o) => (
            <article key={`${o.id}-mobile`} className={styles.mobileCard}>
              <div className={styles.mobileCardTop}>
                <span className={styles.mobileOrderId}>{o.id.slice(-10)}</span>
                <StatusBadge status={o.status} />
              </div>
              <p className={styles.mobileDate}>{o.date}</p>
              <div className={styles.mobileMetaRow}>
                <span className={styles.mobileMetaLabel}>Customer</span>
                <span>{o.customer}</span>
              </div>
              <div className={styles.mobileMetaRow}>
                <span className={styles.mobileMetaLabel}>Product</span>
                <span>{o.product}</span>
              </div>
              <div className={styles.mobileMetaRow}>
                <span className={styles.mobileMetaLabel}>Total</span>
                <span className={styles.mobileValue}>${o.value.toFixed(2)}</span>
              </div>
              <div className={styles.mobileMetaRow}>
                <span className={styles.mobileMetaLabel}>Your Payout</span>
                <span>
                  <span className={styles.payoutAmount}>${o.payout.toFixed(2)}</span>
                  <span className={styles.commissionNote}> (−${o.commission.toFixed(2)} fee)</span>
                </span>
              </div>
              <div className={styles.mobileMetaRow}>
                <span className={styles.mobileMetaLabel}>Payment</span>
                <span className={`${styles.paymentBadge} ${o.isPaid ? styles.paid : styles.unpaid}`}>
                  {o.isPaid ? "Paid" : "Unpaid"} · {o.paymentMethod}
                </span>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPage: (p: number) => void;
}

function Pagination({ page, totalPages, totalItems, pageSize, onPage }: PaginationProps) {
  const from = (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, totalItems);

  const pages: (number | "…")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("…");
    pages.push(totalPages);
  }

  return (
    <div className={styles.pagination}>
      <span className={styles.paginationInfo}>Showing {from}–{to} of {totalItems} orders</span>
      <div className={styles.paginationControls}>
        <button className={styles.pageBtn} onClick={() => onPage(page - 1)} disabled={page === 1} aria-label="Previous page">‹</button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className={styles.pageBtn} style={{ border: "none", cursor: "default" }}>…</span>
          ) : (
            <button key={p} className={`${styles.pageBtn} ${page === p ? styles.active : ""}`} onClick={() => onPage(p as number)}>
              {p}
            </button>
          )
        )}
        <button className={styles.pageBtn} onClick={() => onPage(page + 1)} disabled={page === totalPages} aria-label="Next page">›</button>
      </div>
    </div>
  );
}

// ─── Orders Page ──────────────────────────────────────────────────────────────

export default function CreatorOrders() {
  const [query, setQuery]               = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage]                 = useState(1);
  const [sortKey, setSortKey]           = useState<SortKey>("date");
  const [sortDir, setSortDir]           = useState<SortDir>("desc");
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [isYearMenuOpen, setIsYearMenuOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const handleApply = () => {
      if (!range?.from || !range?.to) return;

      dispatch(
       listUsersOrdersByCreator(
      range.from.toISOString(),
      range.to.toISOString()
    )
  );
  setIsYearMenuOpen(false);
};
  // The action is listUsersOrdersByCreator — its reducer slice is likely named
  // "orderListByCreator". The raw API response is a plain array, so the reducer
  // probably stores it as { loading, error, orders: [...] }.
  const { orders: rawOrders = [], loading, error } = useSelector(
    (state: any) => state.orderList ?? {}
  );

  useEffect(() => {
    dispatch(listUsersOrdersByCreator());
  }, [dispatch]);

  const allOrders: Order[] = useMemo(
    () => (rawOrders as ApiOrder[]).map(mapApiOrder),
    [rawOrders]
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return allOrders
      .filter((o) => {
        const matchQuery =
          !q ||
          o.id.toLowerCase().includes(q) ||
          o.customer.toLowerCase().includes(q) ||
          o.product.toLowerCase().includes(q);
        const matchStatus = !statusFilter || o.status === statusFilter;
        return matchQuery && matchStatus;
      })
      .sort((a, b) => {
        let cmp = 0;
        if (sortKey === "id")     cmp = a.id.localeCompare(b.id);
        if (sortKey === "date")   cmp = a.rawDate.getTime() - b.rawDate.getTime();
        if (sortKey === "value")  cmp = a.value - b.value;
        if (sortKey === "payout") cmp = a.payout - b.payout;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [allOrders, query, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleQueryChange  = (v: string) => { setQuery(v);        setPage(1); };
  const handleStatusChange = (v: string) => { setStatusFilter(v); setPage(1); };

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Orders</h1>
      <p className={styles.pageSubtitle}>Manage and track all your customer orders</p>

      {loading && <p className={styles.stateMsg}><Spinner /></p>}
      {error   && <p className={styles.stateMsg} style={{ color: "red" }}>{error}</p>}

      {!loading && (
        <>
          <SearchSection
            query={query}
            status={statusFilter}
            onQueryChange={handleQueryChange}
            onStatusChange={handleStatusChange}
            resultCount={filtered.length}
            totalCount={allOrders.length}
            range={range}
            setRange={setRange}
            handleApply={handleApply}
            isYearMenuOpen={isYearMenuOpen}
            setIsYearMenuOpen={setIsYearMenuOpen}
          />
          <OrdersTable orders={paginated} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
          <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} />
        </>
      )}
    </div>
  );
}